/**
This component renders a form to edit a brand entity.

1. Renders a form with fields like name, business type, parent/individual brand, etc. based on the brand type.

2. Allows searching and selecting parent brands, individual brands, products, movies, theaters etc. based on the brand type.

3. Handles form validation and error handling.

4. Checks for duplicate brands on save.

5. Saves the edited brand by calling a createOrUpdateBrand action.

6. Handles editing child brands by getting appropriate params.

7. Renders extra fields for movies and theaters like release date, location etc.

8. Handles selection and removal of categories and advertisers.

9. Maintains form state and handles updates via callbacks like onSelect, onChange etc.

10. Shows a confirmation popup before discarding unsaved changes.

It provides an editing UI for the brand entity by fetching relevant data, validating input, checking duplicates and ultimately saving the changes.
 */
import React, { Component } from "react";
import { Intent } from "@blueprintjs/core";
import { isEmpty, omit } from "lodash";
import moment from "moment";
import queryString from "query-string";
import PropTypes from "prop-types";
import { IconNames } from "@blueprintjs/icons";
import { FORM_FIELD_TYPE, BRAND_TYPE, BRANDS_TYPE } from "../../../../constants";
import styles from "../../../../styles/Catalogue/Brands.module.scss";
import {
  searchParentBrands,
  searchIndividualBrands,
  searchProducts,
  searchTags,
  searchMovies,
  searchDuplicateParentBrands,
  searchDuplicateIndividualBrands,
  searchDuplicateProducts,
  searchDuplicateVariants,
} from "../../../../api";
import RightPanel from "../../../common/RightPanel";
import Form from "../../../common/Form";
import { SearchIcon } from "../../../common/SearchIcon";
import { CustomMenuItem } from "../../../common";
import Popup from "../../../common/Popup";
import RemoveIcon from "../../../common/RemoveIcon";
import { ADD_BRAND_TITLE, ERROR_MESSAGE, DEFAULT_BRANDS_PATH } from "./brandConstants";
import DuplicateBrands from "./DuplicateBrands";
import { isSlateAdminCompanyUser } from "../../../../utils";
import { debounce } from "lodash";

export default class EditBrand extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isPopupOpen: false,
      brand: props.brand || { name: "", advertisers: [], categories: [] },
      hasEditError: false,
      errorMessage: {},
      duplicateBrands: [],
      disabled: false,
      entryType: "Parent Brand",
    };
    this.lastNameQuery = "";
  }

  static propTypes = {
    brand: PropTypes.object.isRequired,
  };

  renderEntryTypeSelector = () => {
    const entryOptions = ["Parent Brand", "Individual Brand", "Product", "Variant"];
    return [
      {
        key: "entryType",
        type: FORM_FIELD_TYPE.SELECT,
        title: "Select Entry Type",
        list: entryOptions,
        items: entryOptions,
        selectedList: [this.state.entryType],
        value: this.state.entryType,
        showLabelInButton: false,
        singleSelect: true,
        onSelect: ([value]) => this.handleEntryTypeChange(value),
      },
    ];
  };

  renderSelectedForm = () => {
    const { entryType } = this.state;
    const baseConfig = this.renderConfig();

    const allowedKeysByEntryType = {
      "Parent Brand": ["businessType", "name", "advertisers", "categories"],

      "Individual Brand": ["parentBrandId", "name", "categories"],

      Product: [
        "parentBrandId",
        "individualBrandId",
        "name",
        "productIdentificationNumberTypeId",
        "productIdentificationNumber",
        "categories",
      ],

      Variant: [
        "parentBrandId",
        "individualBrandId",
        "productId",
        "name",
        "variantIdentificationNumberTypeId",
        "variantIdentificationNumber",
      ],
    };

    const allowedKeys = allowedKeysByEntryType[entryType] || [];

    return baseConfig.filter((item) => allowedKeys.includes(item.key));
  };

  handleEntryTypeChange = (type) => {
    let newBrand = {};

    switch (type) {
      case "Parent Brand":
        newBrand = {
          name: "",
          businessType: "",
          advertisers: this.state.brand.advertisers || [],
          categories: [],
        };
        break;

      case "Individual Brand":
        newBrand = {
          name: "",
          parentBrandId: "",
          parentBrandName: "",
          categories: [],
        };
        break;

      case "Product":
        newBrand = {
          name: "",
          parentBrandId: "",
          parentBrandName: "",
          individualBrandId: "",
          individualBrandName: "",
          productIdentificationNumberTypeId: "",
          productIdentificationNumber: "",
          categories: [],
        };
        break;

      case "Variant":
        newBrand = {
          name: "",
          parentBrandId: "",
          parentBrandName: "",
          individualBrandId: "",
          individualBrandName: "",
          productId: "",
          productName: "",
          variantIdentificationNumberTypeId: "",
          variantIdentificationNumber: "",
        };
        break;

      default:
        return;
    }

    this.setState({
      entryType: type,
      brand: newBrand,
      duplicateBrands: [],
      hasEditError: false,
    });
  };

  getEntryTypePlural = () => {
    const { entryType } = this.state;

    switch (entryType) {
      case "Parent Brand":
        return "Parent Brands";
      case "Individual Brand":
        return "Individual Brands";
      case "Product":
        return "Products";
      case "Variant":
        return "Variants";
      default:
        return "Brands";
    }
  };

  getEntryTypeToastKey = (isAutoApproved) => {
    const { entryType } = this.state;

    const successKeys = {
      "Parent Brand": "parentBrandCreatedSuccess",
      "Individual Brand": "individualBrandCreatedSuccess",
      Product: "productCreatedSuccess",
      Variant: "variantCreatedSuccess",
    };

    const approvalKeys = {
      "Parent Brand": "parentBrandSubmittedForApproval",
      "Individual Brand": "individualBrandSubmittedForApproval",
      Product: "productSubmittedForApproval",
      Variant: "variantSubmittedForApproval",
    };

    return isAutoApproved ? successKeys[entryType] : approvalKeys[entryType];
  };

  isAdminOrApprover = () => {
    const { userData } = this.props;
    return isSlateAdminCompanyUser(userData);
  };

  getSelectedTabId = () => {
    switch (this.state.entryType) {
      case "Individual Brand":
        return DEFAULT_BRANDS_PATH.INDIVIDUAL_BRANDS;
      case "Product":
        return DEFAULT_BRANDS_PATH.PRODUCTS;
      case "Variant":
        return DEFAULT_BRANDS_PATH.VARIANTS;
      case "Parent Brand":
      default:
        return DEFAULT_BRANDS_PATH.PARENT_BRANDS;
    }
  };

  searchDuplicatesByEntryType = async (value) => {
    const includeAllStatuses = true;

    switch (this.state.entryType) {
      case "Individual Brand":
        return await searchDuplicateIndividualBrands(
          value,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          includeAllStatuses
        );

      case "Product":
        return await searchDuplicateProducts(
          value,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          includeAllStatuses
        );

      case "Variant":
        return await searchDuplicateVariants(
          value,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          includeAllStatuses
        );

      case "Parent Brand":
      default:
        return await searchDuplicateParentBrands(
          value,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          includeAllStatuses
        );
    }
  };

  extractDuplicateBrands = (response) => {
    const possibleDuplicateSources = [
      response,
      response?.data,
      response?.data?.data,
      response?.data?.results,
      response?.data?.data?.results,
      response?.results,
      response?.duplicateBrands,
      response?.data?.duplicateBrands,
    ];

    return possibleDuplicateSources.find((item) => Array.isArray(item)) || [];
  };

  debouncedDuplicateSearch = debounce(async (value, brand) => {
    try {
      const response = await this.searchDuplicatesByEntryType(value);

      if (value !== this.lastNameQuery) return;

      const duplicates = this.extractDuplicateBrands(response);

      this.setState({
        duplicateBrands: brand?.id
          ? duplicates.filter((duplicateBrand) => duplicateBrand.id !== brand.id)
          : duplicates,
      });
    } catch {
      if (value !== this.lastNameQuery) return;

      this.setState({
        duplicateBrands: [],
      });
    }
  }, 400);

  componentDidMount() {
    const { actions, businessTypes, productIdentificationNumberType } = this.props;

    if (!businessTypes?.list?.length) {
      actions.getBusinessTypesList();
    }

    if (!productIdentificationNumberType?.list?.data?.length) {
      actions.getProductIdentificationNumberTypeList();
    }
  }

  componentWillUnmount() {
    this.debouncedDuplicateSearch.cancel();
  }

  componentDidUpdate(prevProps, prevState) {
    const wasOpen = prevProps.brands?.editBrand?.isOpen;
    const isOpen = this.props.brands?.editBrand?.isOpen;

    if (!wasOpen && isOpen) {
      this.setState({
        brand: this.props.brand || { name: "", advertisers: [], categories: [] },
        entryType: "Parent Brand",
        duplicateBrands: [],
        hasEditError: false,
      });

      this.lastNameQuery = "";
    }

    if (this.state.disabled !== prevState.disabled) {
      this.setState({ disabled: false });
    }
  }

  handlePopup = () => this.setState((prevState) => ({ isPopupOpen: !prevState.isPopupOpen }));
  discardChanges = () => {
    this.handlePopup();
    this.cancelEdit();
  };

  cancelEdit = () => {
    this.setState({
      hasEditError: false,
      brand: { name: "", advertisers: [], categories: [] },
      duplicateBrands: [],
      entryType: "Parent Brand",
    });

    this.lastNameQuery = "";
    this.props.cancelEdit();
  };

  isValid = () => {
    const { brand = {} } = this.state;

    if (
      !(brand.name || "").length ||
      ("businessType" in brand && !(brand.businessType || "").length) ||
      ("parentBrandId" in brand && !(brand.parentBrandId || "").length) ||
      ("individualBrandId" in brand && !(brand.individualBrandId || "").length) ||
      ("productId" in brand && !(brand.productId || "").length) ||
      ("productName" in brand && !(brand.productName || "").length)
    ) {
      return false;
    }
    return true;
  };

  saveBrand = () => {
    const { actions, fetchDataList } = this.props;
    const { brand } = this.state;

    const isAutoApproved = this.isAdminOrApprover();
    const toastKey = this.getEntryTypeToastKey(isAutoApproved);

    actions.createOrUpdateBrand(
      brand,
      {
        ...this.getParams(),
        toastKey,
      },
      this.cancelEdit,
      fetchDataList,
      isAutoApproved,
      this.props.onSuccess
    );
  };

  checkDuplication = async () => {
    const { brand } = this.state;

    if (this.shouldShowCategoryError(brand)) {
      this.setState({ hasEditError: true });
      return;
    }

    if (!this.isValid()) {
      this.setState({ hasEditError: true });
      return;
    }

    try {
      const response = await this.searchDuplicatesByEntryType(brand.name);
      this.hasDuplication(response);
    } catch (error) {
      console.error("Duplicate check failed", error);

      this.setState({
        hasEditError: true,
        duplicateBrands: [],
      });

      if (this.props.showToast) {
        this.props.showToast({
          message: "Unable to check duplicates. Please try again.",
          intent: Intent.DANGER,
        });
      }
    }
  };

  getParams = () => {
    const {
      match: { params },
      history: { location },
    } = this.props;

    const queryParams = queryString.parse(location.search);
    const selectedTabId = this.getSelectedTabId();

    // if (!this.state.brand?.id) {
    //   return {
    //     ...params,
    //     ...queryParams,
    //     tabId: selectedTabId,
    //   };
    // }

    if (!params.tabId) {
      return {
        tabId: selectedTabId,
        ...queryParams,
      };
    }

    if (params.id && params.action === "new") {
      return this.getChildParams(params);
    }

    return { ...params, ...queryParams, tabId: params.tabId || selectedTabId };
  };

  getChildParams = (params) => {
    switch (params.tabId) {
      case BRANDS_TYPE.PARENT_BRANDS:
        return { tabId: BRANDS_TYPE.INDIVIDUAL_BRANDS };
      case BRANDS_TYPE.INDIVIDUAL_BRANDS:
        return { tabId: BRANDS_TYPE.PRODUCTS };
      case BRANDS_TYPE.PRODUCTS:
        return { tabId: BRANDS_TYPE.VARIANTS };
      default:
        return;
    }
  };

  hasDuplication = (response) => {
    const duplicateBrands = this.extractDuplicateBrands(response);

    this.setState(
      {
        duplicateBrands,
      },
      () => {
        if (duplicateBrands.length === 0) {
          this.saveBrand();
        }
      }
    );
  };

  isSelectedCategory = (data) => {
    const { brand } = this.state;
    return (
      brand.categories &&
      brand.categories.find(
        (category) =>
          category.categoryId === data.category.id &&
          category.subCategory1Id === data.subCategory1.id &&
          category.subCategory2Id === data.subCategory2.id
      )
    );
  };

  renderCategoryMenu = (data, { modifiers, handleClick }) => {
    return (
      <CustomMenuItem
        key={`${data.category.id} ${data.subCategory1.id} ${data.subCategory2.id}`}
        active={modifiers.active}
        icon={this.isSelectedCategory(data) ? IconNames.TICK : IconNames.BLANK}
        onClick={handleClick}
        text={this.categoryMenuRenderer(data)}
        shouldDismissPopover={false}
      />
    );
  };

  renderAdvertiserMenu = (data, { modifiers, handleClick }) => {
    const city = data?.cityName || "";
    return (
      <CustomMenuItem
        key={data.id}
        active={modifiers.active}
        icon={this.isSelectedAdvertiser(data) ? IconNames.TICK : IconNames.BLANK}
        onClick={handleClick}
        text={
          <>
            <div>{data.name}</div>
            {city && <div style={{ fontSize: "12px", color: "#8a9ba8" }}>{city}</div>}
          </>
        }
        shouldDismissPopover={false}
      />
    );
  };

  isSelectedAdvertiser = (data) => {
    const { brand } = this.state;
    return brand.advertisers && brand.advertisers.find((advertiser) => advertiser.id === data.id);
  };

  transformData = (data) => {
    return {
      categoryId: data.category.id,
      categoryName: data.category.name,
      subCategory1Id: data.subCategory1.id,
      subCategory1Name: data.subCategory1.name,
      subCategory2Id: data.subCategory2.id,
      subCategory2Name: data.subCategory2.name,
    };
  };

  arrowWithName = (n1, n2, n3) => {
    return [n1, n2, n3].filter((n) => n !== null && n.length > 0 && n !== undefined).join(" > ");
  };

  categoryMenuRenderer = (data) => {
    return this.arrowWithName(data.category.name, data.subCategory1.name, data.subCategory2.name);
  };

  categoryTagRenderer = (data) => {
    return this.arrowWithName(data.categoryName, data.subCategory1Name, data.subCategory2Name);
  };

  selectItem = (data) => {
    const { brand } = this.state;
    this.setState({
      disabled: true,
      brand: { ...brand, ...data },
    });
  };

  onSelect = (item) => {
    const { brand } = this.state;
    this.setState({
      brand: { ...brand, ...item },
    });
  };

  searchMoviesTheatre = async (value, type) => {
    switch (type) {
      case BRAND_TYPE.EXBT:
        const theatres = await searchTags(value, "theatre");
        // Restructure the tag response as a results.
        return { ...theatres, data: { results: theatres.data } }; //  Theatre search
      case BRAND_TYPE.PSTD:
        return await searchMovies(value); // Movies search
      default:
        return "";
    }
  };

  checkMovieOrTheatre = (props, type) => {
    switch (type) {
      case BRAND_TYPE.EXBT:
        return { theatreId: props.id };
      case BRAND_TYPE.PSTD:
        return { movieId: props.id };
      default:
        return "";
    }
  };

  handleUpdateTitle = () => {
    const { brand } = this.state;
    if (brand.productId) return "Variant";
    else if (brand.individualBrandId) return "Product";
    else if (brand.parentBrandId) return "Individual Brand";
    else return "Parent Brand";
  };

  onCategorySelect = (data) => {
    const { brand } = this.state;
    const categories = brand.categories || [];
    const dataClone = this.transformData(data);
    const brandIndex = categories.findIndex(
      (item) =>
        item.categoryName === dataClone.categoryName &&
        item.subCategory1Name === dataClone.subCategory1Name &&
        item.subCategory2Name === dataClone.subCategory2Name
    );
    if (brandIndex >= 0) this.removeCategory(brand, brandIndex);
    else
      this.selectItem({
        categories: [...categories, this.transformData(data)],
      });
  };

  onAdvertiserSelect = (data) => {
    const { brand } = this.state;
    const advertisers = brand.advertisers || [];
    const brandIndex = advertisers.findIndex((item) => item.id === data.id);
    if (brandIndex >= 0) this.removeAdvertiser(brand, brandIndex);
    else
      this.selectItem({
        advertisers: [...advertisers, data],
      });
  };

  removeCategory = (brand, brandIndex) => {
    const newBrand = { ...brand };
    newBrand.categories.splice(brandIndex, 1);
    this.setState({
      brand: newBrand,
      disabled: true,
    });
  };

  removeAdvertiser = (brand, brandIndex) => {
    const newBrand = { ...brand };
    newBrand.advertisers.splice(brandIndex, 1);
    this.setState({
      brand: newBrand,
      disabled: true,
    });
  };

  removeItem = (itemIndex, key) => {
    const { brand } = this.state;
    this.setState({
      brand: {
        ...brand,
        ...{ [key]: brand[key].filter((item, index) => index !== itemIndex) },
      },
    });
  };
  clearMultiSelect = (key) => {
    const { brand } = this.state;
    this.setState({
      brand: {
        ...brand,
        ...{ [key]: [] },
      },
    });
  };
  theatresOrMoviesSubText = (item, type) => {
    switch (type) {
      case BRAND_TYPE.EXBT:
        const { context } = item;
        return [context?.city?.name, context?.province?.name, context?.country?.name]
          .filter(Boolean)
          .join(", ");
      case BRAND_TYPE.PSTD:
        const { info } = item;
        const releaseDate = Object.values(info?.country_specific_release_dates).sort();
        return releaseDate
          ? [info.language, moment(releaseDate[0]).format("YYYY")].filter(Boolean).join(" • ")
          : "";
      default:
        return "";
    }
  };
  renderBrandNameField = () => {
    const { hasEditError, brand } = this.state;
    const { entryType } = this.state;
    const labelName = brand.id ? this.handleUpdateTitle() : entryType;
    if (this.isRenderMovieOrTheatreField()) {
      return {
        key: "name",
        type: FORM_FIELD_TYPE.API_SEARCH,
        title: "Individual Brand Name",
        placeholder: "Search...",
        onSelect: (props) => {
          const value = props.name;
          const updatedBrand = {
            ...this.state.brand,
            name: value,
            ...this.checkMovieOrTheatre(props, brand.businessType || brand.type),
          };
          this.setState({ brand: updatedBrand });
          this.lastNameQuery = value || "";
          if (value?.length > 2) {
            this.debouncedDuplicateSearch(value, updatedBrand);
          }
        },
        onQueryChange: (value) => {
          const updatedBrand = {
            ...this.state.brand,
            name: value,
          };
          this.setState({ brand: updatedBrand });
          this.lastNameQuery = value || "";
          if (value?.length > 2) {
            this.debouncedDuplicateSearch(value, updatedBrand);
          } else {
            this.setState({ duplicateBrands: [] });
          }
        },
        query: brand.name,
        searchIcon: "right",
        showSubText: true,
        selectedItem: {
          name: brand.name,
          ...this.checkMovieOrTheatre({ id: "" }, brand.businessType || brand.type),
        },
        parseResults: (data) => {
          return data.results.map((item) => {
            return {
              id: item.id,
              name: item.displayName || item.name,
              tag: this.theatresOrMoviesSubText(item, brand.businessType || brand.type),
              item: item,
            };
          });
        },
        fetchAction: (text) => this.searchMoviesTheatre(text, brand.businessType || brand.type),
        errorMessage: ERROR_MESSAGE.enterIndividualBrandName,
        error: hasEditError && !brand.name,
        size: 12,
      };
    } else {
      return {
        key: "name",
        type: FORM_FIELD_TYPE.INPUT,
        label: `${labelName} Name`,
        className: "col-12",
        size: 12,
        placeholder: `Enter ${labelName} Name`,
        value: brand.name,
        error: hasEditError && brand.name.trim().length === 0,
        errorMessage: `Enter ${labelName} Name`,
        onChange: (e) => {
          const value = e.target.value;
          const updatedBrand = {
            ...this.state.brand,
            name: value,
          };
          this.setState({ brand: updatedBrand });
          this.lastNameQuery = value || "";
          // this.onSelect({ name: value });
          if (value?.length > 2) {
            this.debouncedDuplicateSearch(value, updatedBrand);
          } else {
            this.setState({
              duplicateBrands: [],
            });
          }
        },
      };
    }
  };
  isRenderMovieOrTheatreField = () => {
    const { brand } = this.state;
    const {
      match: { params },
    } = this.props;
    // It looks too bad. I can't understand what it is. Should change the logic.
    return (
      (([DEFAULT_BRANDS_PATH.INDIVIDUAL_BRANDS].includes(params.tabId) ||
        ([DEFAULT_BRANDS_PATH.PARENT_BRANDS].includes(params.tabId) &&
          params.viewtabId &&
          params.action === "new") ||
        ([DEFAULT_BRANDS_PATH.PARENT_BRANDS].includes(params.tabId) &&
          params.viewtabId === DEFAULT_BRANDS_PATH.INDIVIDUAL_BRANDS)) &&
        [BRAND_TYPE.EXBT, BRAND_TYPE.PSTD].includes(brand.type)) ||
      "movieId" in brand ||
      "theatreId" in brand
    );
  };

  theatreOrMovie = () => {
    const { brand } = this.state;
    if (BRAND_TYPE.EXBT === brand.type) {
      return { name: "", theatreId: "" };
    }
    if (BRAND_TYPE.PSTD === brand.type) {
      return { name: "", movieId: "" };
    }
    return "";
  };
  onBrandSelection = (data) => {
    const { brand } = this.state;
    if ("productId" in brand && "individualBrandId" in brand && "parentBrandId" in brand) {
      return data;
    } else if ("individualBrandId" in brand && "parentBrandId" in brand) {
      return omit(data, ["productId", "productName"]);
    } else if ("parentBrandId" in brand) {
      return omit(data, ["productId", "productName", "individualBrandId", "individualBrandName"]);
    }
  };

  shouldShowCategoryError = (brand) => {
    if (!brand) return false;

    const isParentBrand =
      brand.parentBrandId === undefined &&
      brand.individualBrandId === undefined &&
      brand.productId === undefined;

    return isParentBrand && (!brand.categories || brand.categories.length === 0);
  };

  renderConfig = () => {
    const { hasEditError, brand } = this.state;
    const {
      actions,
      businessTypes: { list },
      brands: { categorySearch, advertiserSearch },
      productIdentificationNumberType: {
        list: { data: productIdentificationNumberType },
      },
    } = this.props;

    const idTypeOptions = (productIdentificationNumberType || []).map((l) => l.type);
    const brandOptions = (list || []).map((l) => l.name);

    const selectedProductIdentificationNumber = brand.productIdentificationNumberTypeId
      ? [
          productIdentificationNumberType.find(
            (data) => data.id === brand.productIdentificationNumberTypeId
          )?.type,
        ]
      : [];
    const selectedVariantIdentificationNumber = brand.variantIdentificationNumberTypeId
      ? [
          productIdentificationNumberType.find(
            (data) => data.id === brand.variantIdentificationNumberTypeId
          )?.type,
        ]
      : [];

    return [
      {
        key: "code",
        type: FORM_FIELD_TYPE.LABEL,
        title: "Code",
        value: brand.code,
      },
      {
        placeHolder: "Select Brand Type",
        key: "businessType",
        type: FORM_FIELD_TYPE.SELECT,
        title: "Brand Type",
        label: "",
        value: brand.businessType,
        list: brandOptions,
        items: brandOptions,
        selectedList: brand.businessType
          ? [list.find((type) => type.code === brand.businessType)?.name]
          : [],
        singleSelect: true,
        error: hasEditError && "businessType" in brand && !(brand.businessType || "").length,
        errorMessage: ERROR_MESSAGE.businessType,
        showLabelInButton: false,
        onSelect: ([value]) => {
          const selected = list.find((type) => type.name === value);
          if (!selected) return;

          this.onSelect({
            businessType: selected.code,
          });
        },
      },
      {
        key: "parentBrandId",
        type: FORM_FIELD_TYPE.API_SEARCH,
        isLabel: Boolean(brand.id),
        title: "Parent Brand",
        placeholder: "Search Parent Brand",
        onSelect: (props) => {
          this.onSelect(
            this.onBrandSelection({
              parentBrandId: props.id,
              parentBrandName: props.name,
              type: props.item.businessType,
              individualBrandId: "",
              individualBrandName: "",
              productId: "",
              productName: "",
              ...this.theatreOrMovie(),
            })
          );
        },
        query: brand.parentBrandName,
        value: brand.parentBrandName,
        selectedItem: { id: brand.parentBrandId, name: brand.parentBrandName },
        searchIcon: "right",
        showSubText: false,
        parseResults: (data) => {
          return (
            data.data &&
            data.data.map((item) => {
              return {
                id: item.id,
                name: item.name,
                tag: item.type,
                item: item,
              };
            })
          );
        },
        fetchAction: (text) => searchParentBrands(text),
        errorMessage: ERROR_MESSAGE.parentBrandName,
        error: hasEditError && !brand.parentBrandName,
      },
      {
        key: "individualBrandId",
        type: FORM_FIELD_TYPE.API_SEARCH,
        isLabel: Boolean(brand.id),
        title: "Individual Brand",
        placeholder: "Search Individual Brand",
        onSelect: (props) => {
          this.onSelect(
            this.onBrandSelection({
              parentBrandId: props?.item.parentBrandId,
              parentBrandName: props?.item.parentBrandName,
              individualBrandId: props.id,
              individualBrandName: props.name,
              productId: "",
              productName: "",
            })
          );
        },
        onQueryChange: (value) => {
          this.onSelect({
            parentBrandId: "",
            parentBrandName: "",
            individualBrandId: "",
            individualBrandName: value,
            productId: "",
            productName: "",
          });
        },
        query: brand.individualBrandName || "",
        value: brand.individualBrandName || "",
        selectedItem: brand.individualBrandId
          ? {
              id: brand.individualBrandId,
              name: brand.individualBrandName,
            }
          : null,
        searchIcon: "right",
        showSubText: false,
        parseResults: (data) => {
          return (
            data.data &&
            data.data.map((item) => {
              return {
                id: item.id,
                name: item.name,
                tag: item.type,
                item: item,
              };
            })
          );
        },
        fetchAction: (text) => searchIndividualBrands(text),
        errorMessage: ERROR_MESSAGE.individualBrandName,
        error: hasEditError && !brand.individualBrandName,
      },
      {
        key: "productId",
        type: FORM_FIELD_TYPE.API_SEARCH,
        isLabel: Boolean(brand.id),
        title: "Product Name",
        placeholder: "Search Product",
        onSelect: (props) => {
          this.onSelect(
            this.onBrandSelection({
              parentBrandId: props?.item.parentBrandId,
              parentBrandName: props?.item.parentBrandName,
              individualBrandId: props?.item.individualBrandId,
              individualBrandName: props?.item.individualBrandName,
              productId: props.id,
              productName: props.name,
            })
          );
        },
        onQueryChange: (value) => {
          this.onSelect({
            parentBrandId: "",
            parentBrandName: "",
            individualBrandId: "",
            individualBrandName: "",
            productId: "",
            productName: value,
          });
        },
        query: brand.productName || "",
        value: brand.productName || "",
        selectedItem: brand.productId
          ? {
              id: brand.productId,
              name: brand.productName,
            }
          : null,
        searchIcon: "right",
        showSubText: false,
        parseResults: (data) => {
          return (
            data.data &&
            data.data.map((item) => {
              return {
                id: item.id,
                name: item.name,
                tag: item.type,
                item: item,
              };
            })
          );
        },
        fetchAction: (text) => searchProducts(text),
        errorMessage: ERROR_MESSAGE.productName,
        error: hasEditError && !brand.productName,
      },
      { ...this.renderBrandNameField() },
      {
        key: "productIdentificationNumberTypeId",
        type: FORM_FIELD_TYPE.SELECT,
        containerClassName: styles.productIdentificationNumber,
        title: "Product Identification Number",
        label: "",
        placeHolder: "Select Type",
        value: brand && brand.productIdentificationNumberTypeId,
        list: idTypeOptions,
        items: idTypeOptions,
        selectedList: selectedProductIdentificationNumber,
        singleSelect: true,
        showLabelInButton: false,
        onSelect: ([value]) => {
          const selected = productIdentificationNumberType.find((data) => data.type === value);
          if (!selected) return;

          this.onSelect({
            productIdentificationNumberTypeId: selected.id,
          });
        },
      },
      {
        key: "productIdentificationNumber",
        type: FORM_FIELD_TYPE.INPUT,
        label: "",
        className: "col-12",
        placeholder: "ID",
        value: brand && brand.productIdentificationNumber,
        onChange: (e) => this.onSelect({ productIdentificationNumber: e.target.value }),
      },
      {
        key: "variantIdentificationNumberTypeId",
        type: FORM_FIELD_TYPE.SELECT,
        containerClassName: styles.productIdentificationNumber,
        title: "Product Identification Number",
        label: "",
        placeHolder: "Select Type",
        value: brand && brand.variantIdentificationNumberTypeId,
        list: idTypeOptions,
        items: idTypeOptions,
        selectedList: selectedVariantIdentificationNumber,
        singleSelect: true,
        showLabelInButton: false,
        onSelect: ([value]) => {
          const selected = productIdentificationNumberType.find((data) => data.type === value);
          if (!selected) return;

          this.onSelect({
            variantIdentificationNumberTypeId: selected.id,
          });
        },
      },
      {
        key: "variantIdentificationNumber",
        type: FORM_FIELD_TYPE.INPUT,
        label: "",
        className: "col-12",
        placeholder: "ID",
        value: brand && brand.variantIdentificationNumber,
        onChange: (e) => this.onSelect({ variantIdentificationNumber: e.target.value }),
      },
      {
        key: "advertisers",
        type: FORM_FIELD_TYPE.MULTI_SELECT,
        label: "Clients",
        items: advertiserSearch,
        onQueryChange: actions.searchAdvertisers,
        itemRenderer: this.renderAdvertiserMenu,
        onItemSelect: (data) => {
          this.onAdvertiserSelect(data);
        },
        resetOnSelect: true,
        isHidden: !!brand.productId,
        tagRenderer: (data) => data.name,
        tagInputProps: {
          placeholder: "Select Clients",
          disabled: this.state.disabled,
          tagProps: { intent: Intent.NONE, minimal: true },
          onRemove: (data, index) => this.removeItem(index, "advertisers"),
          rightElement:
            brand.advertisers && brand.advertisers.length ? (
              <RemoveIcon onClick={() => this.clearMultiSelect("advertisers")} />
            ) : (
              <SearchIcon />
            ),
        },
        selectedItems: brand.advertisers || [],
      },
      {
        key: "categories",
        type: FORM_FIELD_TYPE.MULTI_SELECT,
        label: "Categories",
        items: categorySearch,
        itemRenderer: this.renderCategoryMenu,
        onItemSelect: (data) => {
          this.onCategorySelect(data);
        },
        onQueryChange: actions.categorySearch,
        resetOnSelect: true,
        isHidden: !!brand.productId,
        tagRenderer: this.categoryTagRenderer,
        tagInputProps: {
          placeholder: "Select Categories",
          disabled: this.state.disabled,
          tagProps: { intent: Intent.NONE, minimal: true },
          onRemove: (data, index) => this.removeItem(index, "categories"),
          rightElement:
            brand.categories && brand.categories.length ? (
              <RemoveIcon onClick={() => this.clearMultiSelect("categories")} />
            ) : (
              <SearchIcon />
            ),
        },
        selectedItems: brand.categories || [],
        error: hasEditError && this.shouldShowCategoryError(brand),
        errorMessage: ERROR_MESSAGE.selectCategories,
      },
    ].filter((item) => !item.isHidden);
  };

  handlePrimaryButtonClick = () => {
    const { duplicateBrands, brand } = this.state;

    if (brand?.id) {
      this.saveBrand();
      return;
    }

    if (!isEmpty(duplicateBrands)) {
      this.saveBrand(); // Ignore & Add
    } else {
      this.checkDuplication(); // First check, then user clicks again
    }
  };

  handlePrimaryButtonProps = () => {
    const { duplicateBrands, brand } = this.state;
    const isValid = this.isValid();

    return {
      text: brand.id
        ? "Button.update"
        : !isEmpty(duplicateBrands)
        ? "Button.IgnoreAdd"
        : "Button.add",
      onClick: this.handlePrimaryButtonClick,
      disabled: !isValid,
    };
  };
  handleUpdateBrandTitle = () => {
    const { brand } = this.state;
    if (brand.productId) return "updateVariant";
    else if (brand.individualBrandId) return "updateProduct";
    else if (brand.parentBrandId) return "updateIndividualBrand";
    else return "updateParentBrand";
  };

  render = () => {
    const {
      title,
      brands: { editBrand, isCreateUpdateLoading },
    } = this.props;
    const { brand, isPopupOpen, duplicateBrands } = this.state;
    return (
      <RightPanel
        isOpen={editBrand.isOpen}
        isLoading={isCreateUpdateLoading}
        onClose={() => this.cancelEdit()}
        header={
          brand.id
            ? `RightPanelHeader.${this.handleUpdateBrandTitle()}`
            : `RightPanelHeader.${ADD_BRAND_TITLE[title]}`
        }
        showFooter={true}
        isEdit={brand.id ? true : false}
        primaryButtonProps={this.handlePrimaryButtonProps()}
        secondaryButtonProps={{ text: "Button.cancel", onClick: this.cancelEdit }}
      >
        <>
          <Form config={this.renderEntryTypeSelector()} />
          <Form config={this.renderSelectedForm()} />
        </>
        {!isEmpty(duplicateBrands) && (
          <>
            <div
              style={{
                background: "#f5f6f8",
                borderRadius: "6px",
                padding: "14px 16px",
                marginBottom: "16px",
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "4px",
                  background: "#f5a623",
                  borderTopLeftRadius: "6px",
                  borderBottomLeftRadius: "6px",
                }}
              />

              <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <div
                  style={{
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    background: "#f5a623",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    flexShrink: 0,
                  }}
                >
                  !
                </div>

                <div style={{ fontSize: "14px", lineHeight: "22px" }}>
                  Found <b>{duplicateBrands.length}</b> {this.getEntryTypePlural()} similar to{" "}
                  <b>{brand?.name}</b>. Review the list or continue adding.
                </div>
              </div>
            </div>

            <DuplicateBrands duplicateBrands={duplicateBrands} brand={brand} />
          </>
        )}
        <Popup
          isOpen={isPopupOpen}
          onClose={this.handlePopup}
          onConfirm={this.discardChanges}
          title="Popup.title"
          body="Popup.body"
        />
      </RightPanel>
    );
  };
}
